<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useAlert } from 'dashboard/composables';
import { format } from 'date-fns';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import AccountAPI from 'dashboard/api/account';

import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const { currentAccount, isCloudFeatureEnabled } = useAccount();
const cleanupDialogRef = ref(null);
const cleanupPhrase = ref('');
const cleanupRunning = ref(false);
const cleanupStarting = ref(false);
const cleanupConfirmDisabled = computed(
  () => cleanupPhrase.value !== 'clear' || cleanupStarting.value
);
const {
  captainEnabled,
  captainLimits,
  responseLimits,
  faqLimits,
  fetchLimits,
  isFetchingLimits,
} = useCaptain();

const toMeter = dim => {
  if (!dim || dim.allowed == null) return null;
  return {
    consumed: dim.consumed ?? 0,
    totalCount: dim.allowed,
  };
};

const agentLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.agents)
);
const emailLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.emails)
);
const voiceEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CHANNEL_VOICE)
);
const campaignsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAMPAIGNS)
);
const helpCenterEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.HELP_CENTER)
);
const callingMinutesLimits = computed(() =>
  voiceEnabled.value
    ? toMeter(currentAccount.value?.limits?.calling_minutes)
    : null
);
const campaignSendsLimits = computed(() =>
  campaignsEnabled.value
    ? toMeter(currentAccount.value?.limits?.campaign_sends)
    : null
);
const publishedArticlesLimits = computed(() =>
  helpCenterEnabled.value
    ? toMeter(currentAccount.value?.limits?.published_articles)
    : null
);
const portalsLimits = computed(() =>
  helpCenterEnabled.value
    ? toMeter(currentAccount.value?.limits?.portals)
    : null
);
const storageLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.storage_mb)
);
const contactLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.contacts)
);
const automationLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.automations)
);
const macroLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.macros)
);
const inboxLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.inboxes)
);
const webWidgetLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.web_widgets)
);
const cannedResponseLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.canned_responses)
);
const translationCharacterLimits = computed(() =>
  toMeter(currentAccount.value?.limits?.translation_characters)
);
const retentionDays = computed(() => {
  const days = currentAccount.value?.limits?.retention_days?.allowed;
  return days == null ? null : String(days);
});

const uiFlags = useMapGetter('accounts/getUIFlags');

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

const planName = computed(() => {
  return customAttributes.value.plan_name;
});

const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  return format(endDate, 'dd MMM, yyyy');
});

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const fetchStorageCleanup = async () => {
  try {
    const response = await AccountAPI.getStorageCleanup();
    cleanupRunning.value = Boolean(response.data?.running);
  } catch (_error) {
    cleanupRunning.value = false;
  }
};

const openCleanupDialog = () => {
  if (cleanupRunning.value) return;

  cleanupPhrase.value = '';
  cleanupDialogRef.value?.open();
};

const startCleanup = async () => {
  if (cleanupRunning.value || cleanupPhrase.value !== 'clear') return;

  cleanupStarting.value = true;
  try {
    await AccountAPI.startStorageCleanup();
    cleanupRunning.value = true;
    cleanupDialogRef.value?.close();
  } catch (error) {
    if (error?.response?.status === 409) {
      cleanupRunning.value = true;
      cleanupDialogRef.value?.close();
      return;
    }
    useAlert(t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.ERROR'));
  } finally {
    cleanupStarting.value = false;
  }
};

onMounted(() => {
  fetchLimits();
  fetchStorageCleanup();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <BillingCard
          :title="$t('BILLING_SETTINGS.CURRENT_PLAN.CARD_TITLE')"
          :description="$t('BILLING_SETTINGS.CURRENT_PLAN.CARD_DESCRIPTION')"
        >
          <div
            v-if="planName || subscribedQuantity || subscriptionRenewsOn"
            class="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="planName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-if="
            agentLimits ||
            emailLimits ||
            callingMinutesLimits ||
            campaignSendsLimits ||
            publishedArticlesLimits ||
            portalsLimits ||
            storageLimits ||
            contactLimits ||
            automationLimits ||
            macroLimits ||
            inboxLimits ||
            webWidgetLimits ||
            cannedResponseLimits ||
            translationCharacterLimits ||
            retentionDays
          "
          :title="$t('BILLING_SETTINGS.USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.USAGE.DESCRIPTION')"
        >
          <div v-if="agentLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.AGENTS')"
              v-bind="agentLimits"
            />
          </div>
          <div v-if="emailLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.EMAILS')"
              :hint="$t('BILLING_SETTINGS.USAGE.EMAILS_HINT')"
              v-bind="emailLimits"
            />
          </div>
          <div v-if="callingMinutesLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.CALLING_MINUTES')"
              v-bind="callingMinutesLimits"
            />
          </div>
          <div v-if="campaignSendsLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.CAMPAIGN_SENDS')"
              v-bind="campaignSendsLimits"
            />
          </div>
          <div v-if="publishedArticlesLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.PUBLISHED_ARTICLES')"
              v-bind="publishedArticlesLimits"
            />
          </div>
          <div v-if="portalsLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.PORTALS')"
              v-bind="portalsLimits"
            />
          </div>
          <div v-if="storageLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.STORAGE')"
              v-bind="storageLimits"
            />
            <div class="flex items-center gap-3 mt-3">
              <span
                class="inline-flex items-center gap-1 text-xs normal-case text-n-slate-11"
              >
                <span
                  class="size-4"
                  :class="
                    cleanupRunning
                      ? 'i-lucide-loader-circle animate-spin'
                      : 'i-lucide-circle'
                  "
                />
                {{
                  cleanupRunning
                    ? $t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.RUNNING')
                    : $t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.IDLE')
                }}
              </span>
              <ButtonV4
                :label="$t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.BUTTON')"
                size="sm"
                variant="faded"
                :disabled="cleanupRunning"
                @click="openCleanupDialog"
              />
            </div>
          </div>
          <div v-if="contactLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.CONTACTS')"
              v-bind="contactLimits"
            />
          </div>
          <div v-if="automationLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.AUTOMATIONS')"
              v-bind="automationLimits"
            />
          </div>
          <div v-if="macroLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.MACROS')"
              v-bind="macroLimits"
            />
          </div>
          <div v-if="inboxLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.INBOXES')"
              :hint="$t('BILLING_SETTINGS.USAGE.INBOXES_HINT')"
              v-bind="inboxLimits"
            />
          </div>
          <div v-if="webWidgetLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.WEB_WIDGETS')"
              v-bind="webWidgetLimits"
            />
          </div>
          <div v-if="cannedResponseLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.CANNED_RESPONSES')"
              v-bind="cannedResponseLimits"
            />
          </div>
          <div v-if="translationCharacterLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.USAGE.TRANSLATION_CHARACTERS')"
              :hint="$t('BILLING_SETTINGS.USAGE.TRANSLATION_CHARACTERS_HINT')"
              v-bind="translationCharacterLimits"
            />
          </div>
          <div v-if="retentionDays" class="px-5">
            <DetailItem
              :label="$t('BILLING_SETTINGS.USAGE.RETENTION')"
              :value="
                $t('BILLING_SETTINGS.USAGE.RETENTION_VALUE', {
                  days: retentionDays,
                })
              "
            />
          </div>
        </BillingCard>
        <BillingCard
          v-if="captainEnabled"
          :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
          :description="$t('BILLING_SETTINGS.CAPTAIN.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              flushed
              slate
              icon="i-lucide-refresh-cw"
              :is-loading="isFetchingLimits"
              @click="fetchLimits"
            >
              {{ $t('BILLING_SETTINGS.CAPTAIN.REFRESH_CREDITS') }}
            </ButtonV4>
          </template>
          <div v-if="captainLimits && responseLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.RESPONSES')"
              v-bind="responseLimits"
            />
          </div>
          <div v-if="faqLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.FAQS')"
              v-bind="faqLimits"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-else
          :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
          :description="$t('BILLING_SETTINGS.CAPTAIN.UPGRADE')"
        />

        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
    </template>
  </SettingsLayout>
  <Dialog
    ref="cleanupDialogRef"
    type="alert"
    :title="$t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.DIALOG_TITLE')"
    :description="
      $t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.DIALOG_DESCRIPTION')
    "
    :confirm-button-label="$t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.CONFIRM')"
    :disable-confirm-button="cleanupConfirmDisabled"
    :is-loading="cleanupStarting"
    @confirm="startCleanup"
  >
    <Input
      v-model="cleanupPhrase"
      :label="$t('BILLING_SETTINGS.USAGE.STORAGE_CLEANUP.PHRASE_LABEL')"
      placeholder="clear"
    />
  </Dialog>
</template>
